import { useEffect, useMemo, useRef, useState } from "react";
import { Newspaper, FileSearch, Globe, Video, ShieldAlert, Check, type LucideIcon } from "lucide-react";
import type { AgentKey, AgentStatus, SwarmState } from "@/lib/swarmTypes";
import { NovaMark } from "./brand";

/**
 * GatheringSignals - the big, friendly, non-technical loading hero shown WHILE
 * the swarm is running (before the threat assessment + battlecard are ready).
 *
 * Goal: a customer who has never heard the words "agent", "SSE" or "LLM" should
 * instantly understand that NovaSynth is out gathering real signals to build
 * their brief, see it visibly working, and feel a little delight. So instead of
 * a tiny spinner we show:
 *   - a hero-scale on-brand "smoky" radar visual (pulsing cyan/brand glow blobs
 *     + expanding ping rings + the NovaSynth mark), reduced-motion-safe;
 *   - a headline that personalises to the competitor;
 *   - rotating, plain-language, slightly-funny status lines (every ~2.5s);
 *   - plain-English per-step progress (Scanning news, Reading filings, Checking
 *     the web, Watching videos, Scoring the threat) wired to the REAL agent
 *     statuses so the steps light up live as each specialist finishes;
 *   - a warm completion beat once everything is in.
 *
 * No infra terms, no jargon, no emojis (lucide icons only).
 */

interface GatheringSignalsProps {
  competitor: string;
  agents: SwarmState["agents"];
  /** True once the run is finished AND the threat + battlecard have arrived. */
  isComplete: boolean;
  /** Which intel specialists are enabled (mirrors the profile signal toggles). */
  signals?: { video?: boolean; document?: boolean; web?: boolean; news?: boolean };
}

interface StepDef {
  key: AgentKey | "threat";
  label: string;
  Icon: LucideIcon;
}

// Plain-English steps in the order a person reads them. "threat" is the
// synthesis step - it has no agent slot, so it's derived (see deriveStatus).
const STEPS: StepDef[] = [
  { key: "news", label: "Scanning the news", Icon: Newspaper },
  { key: "document", label: "Reading the filings", Icon: FileSearch },
  { key: "web", label: "Checking the web", Icon: Globe },
  { key: "video", label: "Watching the videos", Icon: Video },
  { key: "threat", label: "Scoring the threat", Icon: ShieldAlert },
];

// Catchy, slightly-funny, jargon-free status lines. {c} is replaced with the
// competitor name. Kept honest (we really are reading press releases, filings
// and demo videos) so the humour never overpromises.
const STATUS_LINES = [
  "Reading every press release so you don't have to",
  "Tailing {c}'s every move (legally)",
  "Digging through filings and demo videos",
  "Teaching the robots to read SEC filings",
  "Sizing up {c} from every angle",
  "Watching the keynotes at 2x so you can skip them",
  "Cross-referencing pricing pages and review sites",
  "Connecting the dots across news, web and video",
  "Asking: should we be worried about {c}?",
  "Turning a thousand signals into one clear brief",
];

const ROTATE_MS = 2500;

export function GatheringSignals({ competitor, agents, isComplete, signals }: GatheringSignalsProps) {
  const name = competitor.trim() || "this competitor";

  // Build the visible step list (respecting disabled signals; threat always shown).
  const steps = useMemo(
    () => STEPS.filter((s) => s.key === "threat" || signals == null || signals[s.key] !== false),
    [signals],
  );

  // Rotating status line. Pauses naturally once complete (we swap to the beat).
  const lines = useMemo(
    () => STATUS_LINES.map((l) => l.replace(/\{c\}/g, name)),
    [name],
  );
  const [lineIdx, setLineIdx] = useState(0);
  const reduceRef = useRef(false);

  useEffect(() => {
    reduceRef.current = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  }, []);

  useEffect(() => {
    if (isComplete) return;
    const id = setInterval(() => setLineIdx((i) => (i + 1) % lines.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [isComplete, lines.length]);

  // Completion beat: a brief warm "Got it." before the brief reveals.
  if (isComplete) {
    return <CompletionBeat name={name} />;
  }

  return (
    <section
      data-print="hide"
      className="ns-glass ns-reveal rounded-2xl shadow-card-lg overflow-hidden"
      role="status"
      aria-live="polite"
      aria-label={`Gathering competitive signals on ${name}`}
    >
      {/* ── Animated hero visual ── */}
      <div className="relative flex flex-col items-center justify-center px-6 pt-12 pb-8 overflow-hidden">
        {/* Smoky drifting glow blobs - the "gathering" mood, reduced-motion-safe */}
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <span
            className="ns-smoke ns-smoke-a"
            style={{ width: "16rem", height: "16rem", left: "12%", top: "8%", background: "radial-gradient(circle, rgba(34,211,238,0.30), transparent 70%)" }}
          />
          <span
            className="ns-smoke ns-smoke-b"
            style={{ width: "15rem", height: "15rem", right: "10%", top: "14%", background: "radial-gradient(circle, rgba(74,86,232,0.30), transparent 70%)" }}
          />
          <span
            className="ns-smoke ns-smoke-c"
            style={{ width: "13rem", height: "13rem", left: "38%", bottom: "0%", background: "radial-gradient(circle, rgba(168,85,247,0.22), transparent 70%)" }}
          />
        </div>

        {/* Radar core: expanding ping rings behind the brand mark */}
        <div className="relative flex items-center justify-center mb-6" aria-hidden="true">
          <div className="absolute inset-0 flex items-center justify-center">
            {[0, 0.9, 1.8].map((delay) => (
              <span
                key={delay}
                className="ns-ping-ring absolute rounded-full border border-[#22d3ee]/40"
                style={{ width: "5.5rem", height: "5.5rem", animationDelay: `${delay}s` }}
              />
            ))}
          </div>
          <span className="relative flex h-20 w-20 items-center justify-center rounded-2xl bg-brand-700/25 border border-[#22d3ee]/40 text-[#22d3ee] shadow-glow-cyan">
            <NovaMark className="w-9 h-9" />
          </span>
        </div>

        {/* Headline + rotating funny status line */}
        <div className="relative text-center max-w-lg">
          <h3 className="font-mono text-xl sm:text-2xl font-bold text-white tracking-tight">
            Gathering signals on <span className="text-[#22d3ee]">{name}</span>
          </h3>
          <p
            key={lineIdx}
            className="ns-line-enter mt-2.5 text-sm sm:text-base text-slate-300 min-h-[1.5rem]"
          >
            {lines[lineIdx]}
          </p>
          <p className="mt-3 text-xs text-slate-500 leading-relaxed">
            We are reading real news, filings, web pages and videos right now - then we will fuse it
            all into one threat-scored brief, just for you.
          </p>
        </div>
      </div>

      {/* ── Plain-English per-step progress ── */}
      <div className="border-t border-white/10 bg-[#091020]/40 px-4 sm:px-6 py-4">
        <ul className="flex flex-col sm:flex-row sm:flex-wrap gap-2.5 sm:gap-x-5 sm:gap-y-2.5 justify-center">
          {steps.map((step) => (
            <StepPill key={step.key} step={step} status={deriveStatus(step.key, agents)} />
          ))}
        </ul>
      </div>
    </section>
  );
}

// ── Per-step status ─────────────────────────────────────────────────────────

type StepStatus = "waiting" | "working" | "done";

/**
 * Map a step to a plain status using the REAL agent slots:
 *   - the 4 intel steps mirror their agent's status directly;
 *   - "threat" (the synthesis step) is derived - it's "done" only once every
 *     intel agent has finished, "working" once at least one has, else "waiting".
 */
function deriveStatus(key: AgentKey | "threat", agents: SwarmState["agents"]): StepStatus {
  if (key !== "threat") {
    return toStepStatus(agents[key].status);
  }
  const all = (Object.keys(agents) as AgentKey[]).map((k) => agents[k].status);
  if (all.length > 0 && all.every((s) => s === "done" || s === "error")) return "done";
  if (all.some((s) => s === "done")) return "working";
  return "waiting";
}

function toStepStatus(s: AgentStatus): StepStatus {
  if (s === "done") return "done";
  if (s === "running") return "working";
  if (s === "error") return "done"; // settled - don't leave it spinning forever
  return "waiting";
}

function StepPill({ step, status }: { step: StepDef; status: StepStatus }) {
  const { Icon, label } = step;

  const tone =
    status === "done"
      ? "border-emerald-700/40 bg-emerald-500/10 text-emerald-300"
      : status === "working"
      ? "border-[#22d3ee]/35 bg-[#22d3ee]/10 text-[#22d3ee]"
      : "border-[#1a2640] bg-white/[0.03] text-slate-500";

  return (
    <li
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-300 ${tone}`}
      aria-label={`${label}: ${status === "done" ? "done" : status === "working" ? "in progress" : "waiting"}`}
    >
      {status === "done" ? (
        <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      ) : status === "working" ? (
        <span className="relative flex w-2 h-2 shrink-0" aria-hidden="true">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22d3ee] opacity-60" />
          <span className="relative inline-flex rounded-full w-2 h-2 bg-[#22d3ee]" />
        </span>
      ) : (
        <Icon className="w-3.5 h-3.5 shrink-0 opacity-70" aria-hidden="true" />
      )}
      <span>{label}</span>
    </li>
  );
}

// ── Completion beat ───────────────────────────────────────────────────────────

/** A short, warm "we're done" moment shown right before the brief reveals. */
function CompletionBeat({ name }: { name: string }) {
  return (
    <section
      data-print="hide"
      className="ns-glass ns-reveal rounded-2xl shadow-card overflow-hidden"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-4 px-6 py-6">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-600/40 bg-emerald-500/10 text-emerald-300 shrink-0 shadow-glow-green">
          <Check className="w-6 h-6" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="font-mono text-lg font-bold text-white leading-tight">Got it.</p>
          <p className="mt-1 text-sm text-slate-300 leading-relaxed">
            The analysis on <span className="text-emerald-300 font-medium">{name}</span> is complete -
            building your customized brief.
          </p>
        </div>
      </div>
    </section>
  );
}
